// App — root provider stack. Order is fixed:
//   ErrorBoundary → SupabaseProvider → QueryClientProvider → RequireAdmin → RouterProvider
// Reordering breaks auth (RequireAdmin must be inside Query so it can fire fn invokes)
// or breaks query caching (the Query client must wrap RequireAdmin's effects).

import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { ErrorBoundary } from 'react-error-boundary';
import { SupabaseProvider } from './components/auth/SupabaseProvider';
import { RequireAdmin } from './components/auth/RequireAdmin';
import { ShellError } from './components/feedback/ShellError';
import { KeyboardHelpDialog } from './components/layout/KeyboardHelpDialog';
import { captureException } from './lib/observability';
import { queryClient } from './lib/queryClient';
import { router } from './router';

export function App(): JSX.Element {
  return (
    <ErrorBoundary
      FallbackComponent={ShellError}
      onError={(error, info) =>
        captureException(error, {
          componentStack: info.componentStack,
          tags: { boundary: 'shell' },
        })
      }
    >
      <SupabaseProvider>
        <QueryClientProvider client={queryClient}>
          <RequireAdmin>
            <KeyboardHelpDialog>
              <RouterProvider router={router} context={{ queryClient }} />
            </KeyboardHelpDialog>
          </RequireAdmin>
        </QueryClientProvider>
      </SupabaseProvider>
    </ErrorBoundary>
  );
}
